import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import axios from "axios";
import _ from "lodash";
import { Breadcrumbs, Header, Workspace, LoadingState } from "components/Shared";
import { getAuthTokenFromCookie } from "lib/hooks";
import Datasource from "lib/datasource";
import { getCondition, configureConditionSigners } from "actions/conditions";

// Backend stores the signing order as "any" (parallel) or "sequential". The
// UI surfaces them as Parallel / Sequential — see options below.
const SIGNING_ORDERS: { value: string; label: string; description: string }[] = [
	{ value: "any", label: "Parallel", description: "All signers can sign simultaneously" },
	{ value: "sequential", label: "Sequential", description: "Signers sign in the order listed" },
];

const SignerConfig = ({ match, history }: RouteComponentProps<{ id: string }>) => {
	const dispatch = useDispatch();
	const conditionId = match.params.id;
	const condition = useSelector((state: any) => _.find(state.conditions, { id: conditionId }) as any);
	const [candidates, setCandidates] = useState<any[]>([]);
	const [selected, setSelected] = useState<string[]>([]);
	const [signingOrder, setSigningOrder] = useState<string>("any");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		dispatch(getCondition(conditionId) as any);
	}, [dispatch, conditionId]);

	// Pull eligible signers from the condition itself. The backend restricts
	// to (a) project administrators and (b) users connected to this task by
	// role — so this list will not include random project members who have
	// no role on the task.
	useEffect(() => {
		if (!conditionId) return;
		axios
			.get(`${process.env.REACT_APP_API_ENDPOINT_V2}conditions/${conditionId}/signer_candidates`, {
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${getAuthTokenFromCookie()}`
				}
			})
			.then((res) => {
				const users = Datasource.getNormalizedData(res.data, "user") as any[];
				setCandidates(_.uniqBy((users || []).filter(Boolean), "id"));
			})
			.catch(() => setCandidates([]));
	}, [conditionId]);

	// Pre-fill from the condition's current configuration exactly once. This page
	// is shown on every generate — including a re-generate after an Undo — so on
	// the second pass the previously chosen signing order and signer list (in
	// signing order) are already selected and the user can just click Assign to
	// keep them, or change either one.
	//
	// Prefill runs once, not on every `condition` change: getCondition resolving
	// after mount would otherwise wipe a selection the user had already started
	// editing. Signers arrive ordered by position from the API (the association
	// is scoped `live.ordered`), but sort defensively — `selected`'s array order
	// IS the submitted signing order, so a scrambled prefill would silently
	// re-order the signers on save.
	const prefilled = useRef(false);
	useEffect(() => {
		if (!condition || prefilled.current) return;
		const signers = (condition.signers || []) as any[];
		// Child "sign_form" conditions are the newer model; Signer rows are the
		// legacy one. Either can be the populated side depending on when the
		// condition was configured, so fall back rather than showing nothing.
		const fromSigners = _.sortBy(signers, (s: any) => (s.position == null ? Infinity : s.position))
			.map((s: any) => s.user_id);
		const fromChildren = _.sortBy(
			(condition.children || []) as any[],
			(c: any) => (c.position == null ? Infinity : c.position)
		).map((c: any) => _.get(c, "user.id"));
		const preselected = _.uniq((fromSigners.length ? fromSigners : fromChildren).filter(Boolean));
		// Nothing configured yet (first-time flow): leave the empty defaults and
		// stay unlocked, so a later-arriving condition can still prefill.
		if (!preselected.length) return;
		setSelected(preselected);
		// "any" is the backend's parallel value and this component's default, so
		// only override when the condition carries an explicit order.
		if (condition.signing_order) setSigningOrder(condition.signing_order);
		prefilled.current = true;
	}, [condition]);

	if (!condition) return <LoadingState />;

	// Any deliberate edit closes the prefill window as well, so a condition
	// refresh landing mid-edit can't overwrite what the user just chose.
	const toggle = (userId: string) => {
		prefilled.current = true;
		setSelected((list) => (list.includes(userId) ? list.filter((x) => x !== userId) : [...list, userId]));
	};

	const chooseSigningOrder = (value: string) => {
		prefilled.current = true;
		setSigningOrder(value);
	};

	const onSubmit = async () => {
		setSubmitting(true);
		setError(null);
		try {
			await dispatch(
				configureConditionSigners(conditionId, selected, signingOrder) as any
			);
			// Land on the Conditions tab (Tabs reads the active tab from the URL
			// hash) rather than the default Comments tab.
			history.push(`/tasks/${condition.task_id || _.get(condition, "task.id")}#conditions`);
		} catch (e) {
			setError((e as any)?.message || "Save failed");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<Breadcrumbs
				items={[
					// Deep-link to the Conditions tab: Workspace reads the active tab
					// from the URL hash, so a bare /tasks/:id would land the user on
					// the default Comments tab instead of the list this form came from.
					{
						title: "tasks",
						link: condition.task_id ? `/tasks/${condition.task_id}#conditions` : "/tasks"
					},
					{ title: condition.name || "condition" },
					{ title: "Configure signers" }
				]}
			/>
			<Header title="Configure signers" />
			<Workspace id="condition-signers">
				<div style={{ maxWidth: 760, margin: "0 auto" }}>
					{/* Signing order — Parallel (default) or Sequential. Influences the
					    per-signer indicator below: ✓ in parallel mode, position number
					    (1, 2, …) in sequential mode based on click order. */}
					<div className="mb-4">
						<h4 className="mb-1">Signing Order</h4>
						<p className="text-muted mb-3">How signers should sign the form</p>
						<div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
							{SIGNING_ORDERS.map((opt) => {
								const isSelected = signingOrder === opt.value;
								return (
									<div
										key={opt.value}
										onClick={() => chooseSigningOrder(opt.value)}
										style={{
											flex: "1 1 240px",
											padding: "16px 20px",
											border: `1px solid ${isSelected ? "#00C6D7" : "#E5E7EB"}`,
											borderRadius: 8,
											cursor: "pointer",
											background: "#fff",
											transition: "border-color 0.15s ease",
											display: "flex",
											alignItems: "flex-start",
											gap: 14,
										}}
									>
										<span
											style={{
												display: "inline-flex",
												alignItems: "center",
												justifyContent: "center",
												width: 22,
												height: 22,
												borderRadius: "50%",
												background: isSelected ? "#00C6D7" : "transparent",
												border: `1.5px solid ${isSelected ? "#00C6D7" : "#D1D5DB"}`,
												flexShrink: 0,
												marginTop: 2,
											}}
											aria-hidden="true"
										>
											{isSelected && (
												<span
													style={{
														width: 8,
														height: 8,
														borderRadius: "50%",
														background: "#fff",
													}}
												/>
											)}
										</span>
										<div>
											<div style={{ fontWeight: 500, color: "#111827" }}>{opt.label}</div>
											<div className="text-muted small">{opt.description}</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>

					<div className="mb-4">
						<h4 className="mb-1">Select Signers</h4>
						<p className="text-muted mb-0">
							{signingOrder === "sequential"
								? "Choose who needs to sign — the order you select them is the order they sign in"
								: "Choose who needs to sign this form"}
						</p>
					</div>

					<div className="text-muted text-uppercase small mb-2" style={{ letterSpacing: "0.05em" }}>
						Eligible Signers
					</div>
					{candidates.length === 0 && (
						<p className="text-muted small">No eligible signers found — project administrators and users with a role on this task can be assigned</p>
					)}
					{candidates.map((user) => {
						const selectionIndex = selected.indexOf(user.id);
						const isSelected = selectionIndex !== -1;
						const isSequential = signingOrder === "sequential";
						// Indicator shows ✓ in parallel mode, the 1-based position
						// (the order this user was clicked) in sequential mode.
						const indicatorContent = !isSelected
							? ""
							: isSequential
								? String(selectionIndex + 1)
								: "✓";
						return (
							<div
								key={user.id}
								onClick={() => toggle(user.id)}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									padding: "16px 20px",
									marginBottom: 12,
									border: `1px solid ${isSelected ? "#00C6D7" : "#E5E7EB"}`,
									borderRadius: 8,
									cursor: "pointer",
									background: "#fff",
									transition: "border-color 0.15s ease",
								}}
							>
								<div style={{ display: "flex", alignItems: "center", gap: 14 }}>
									<span
										style={{
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											width: 22,
											height: 22,
											borderRadius: 4,
											background: isSelected ? "#00C6D7" : "transparent",
											border: `1.5px solid ${isSelected ? "#00C6D7" : "#D1D5DB"}`,
											color: "#fff",
											fontSize: isSelected && isSequential ? 12 : 14,
											fontWeight: isSelected && isSequential ? 600 : 400,
											lineHeight: 1,
											flexShrink: 0,
										}}
										aria-hidden="true"
									>
										{indicatorContent}
									</span>
									<div>
										<div style={{ fontWeight: 500, color: "#111827" }}>
											{user.name || [user.firstname, user.surname].filter(Boolean).join(" ") || user.email}
										</div>
										{user.title && (
											<div className="text-muted small">{user.title}</div>
										)}
									</div>
								</div>
								<div className="text-muted small">{user.email}</div>
							</div>
						);
					})}

					{error && <div className="alert alert-danger py-2 mt-4">{error}</div>}
					<div className="text-right">
						<button
							className="btn btn-primary"
							disabled={submitting || selected.length === 0}
							onClick={onSubmit}
						>
							{submitting ? "Assigning…" : "Assign"}
						</button>
					</div>
				</div>
			</Workspace>
		</>
	);
};

export default withRouter(SignerConfig);
